'use client'

import * as Sentry from '@sentry/nextjs'
import { AlertCircle, Eye, EyeOff, Loader2 } from 'lucide-react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense,useEffect, useState } from 'react'
import { useFormStatus } from 'react-dom'
import { useForm } from 'react-hook-form'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { useAuth } from '@/contexts/AuthContext'
import kratos from '@/utils/ory-client'

function SignupFormContent() {
  const [registrationFlow, setRegistrationFlow] = useState<any>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const { refreshSession } = useAuth()

  useEffect(() => {
    const from = searchParams.get('from')
    if (from !== 'idx') return

    const metricGuardKey = 'used_secret_landing_IDE_cta_sent'
    if (window.sessionStorage.getItem(metricGuardKey) === '1') return

    try {
      if (typeof Sentry.metrics?.count === 'function') {
        Sentry.metrics.count('used_secret_landing_IDE_cta', 1, {
          attributes: { from: 'idx' }
        })
      }
      window.sessionStorage.setItem(metricGuardKey, '1')
    } catch (metricErr) {
      console.error('[Sentry] Failed to send used_secret_landing_IDE_cta metric', metricErr)
    }
  }, [searchParams])
  
  const form = useForm({
    defaultValues: {
      fullName: '',
      email: '',
      password: ''
    }
  })

  // Google OIDC registration function
  const startOidcRegistration = async (returnTo?: string) => {
    try {
      setIsGoogleLoading(true)
      setErrorMessage(null)
      
      // 1) Start a browser registration flow — returnTo callback so we can record signup metric after OIDC redirect
      const { data: flow } = await kratos.createBrowserRegistrationFlow({ 
        returnTo: returnTo || `${window.location.origin}/auth/signup-callback`
      })

      // Get CSRF token from the flow
      const csrfToken = (flow.ui.nodes as any).find(
        (node: any) => node.attributes.name === 'csrf_token'
      )?.attributes?.value

      if (!csrfToken) {
        setErrorMessage('Security token missing. Please refresh the page.')
        return
      }

      // 2) Submit the flow using OIDC (google). Kratos will respond with a redirect url.
      try {
        const res = await kratos.updateRegistrationFlow({
          flow: flow.id,
          updateRegistrationFlowBody: {
            method: 'oidc',
            provider: 'google',
            // csrf_token not needed for OIDC on browser flows
          },
        })

        // Some setups may return 200 with the URL:
        const okUrl = (res.data as any)?.redirect_browser_to
        if (okUrl) {
          window.location.href = okUrl
          return
        }

        // Fallthrough — handled in catch
      } catch (err: any) {
        const url = err?.response?.data?.redirect_browser_to
        if (err?.response?.status === 422 && url) {
          window.location.href = url   // <-- the expected path
          return
        }
        console.error('Flow Ory Error:', err.response || err)
        setErrorMessage(
          err?.response?.data?.error?.message ||
            'Failed to start Google registration. Please try again.'
        )
      }
    } catch (error: any) {
      console.error('Google OIDC registration failed:', error.response || error)
      setErrorMessage(
        error.response?.data?.error?.message ||
        'Failed to start Google registration. Please try again.'
      )
    } finally {
      setIsGoogleLoading(false)
    }
  }

  useEffect(() => {
    async function initializeRegistrationFlow() {
      try {
        const flowId = searchParams.get('flow')
        let flow

        if (flowId) {
          flow = await kratos.getRegistrationFlow(
            {
              id: flowId
            },
            {
              withCredentials: true,
              headers: {
                Accept: 'application/json'
              }
            }
          )
        } else {
          flow = await kratos.createBrowserRegistrationFlow(
            {
              returnTo: window.location.origin
            },
            {
              withCredentials: true,
              headers: {
                Accept: 'application/json'
              }
            }
          )
        }

        setRegistrationFlow(flow.data)
      } catch (error: any) {
        console.error(
          'Failed to initialize registration flow:',
          error.response || error
        )
        const message = String(
          error?.response?.data?.error?.message ??
            error?.response?.data?.error?.reason ??
            ''
        ).toLowerCase()
        if (message.includes('already logged in')) {
          router.replace('/')
          return
        }
        setErrorMessage(
          error.response?.data?.error?.message ||
            'Failed to initialize registration. Please try again.'
        )
      }
    }

    initializeRegistrationFlow()
  }, [searchParams])

  const onSubmit = async (formData: any) => {
    if (!registrationFlow) return

    try {
      setIsSubmitting(true)
      if (!acceptedTerms) {
        setErrorMessage('Please accept the terms of service and privacy policy.')
        setIsSubmitting(false)
        return
      }

      // Naively split fullName into first and last
      const [firstName, ...rest] = formData.fullName.trim().split(' ')
      const lastName = rest.join(' ') || ''

      if (!firstName) {
        setErrorMessage('Please enter your first name.')
        setIsSubmitting(false)
        return
      }

      // Clear any existing error messages
      setErrorMessage(null)

      // Get CSRF token from the flow
      const csrfToken = registrationFlow.ui.nodes.find(
        (node: any) => node.attributes.name === 'csrf_token'
      )?.attributes.value

      if (!csrfToken) {
        setErrorMessage('Security token missing. Please refresh the page.')
        setIsSubmitting(false)
        return
      }

      // Update registration flow using Ory client
      await kratos.updateRegistrationFlow(
        {
          flow: registrationFlow.id,
          updateRegistrationFlowBody: {
            method: 'password',
            traits: {
              email: formData.email,
              name: {
                first: firstName,
                last: lastName
              }
            },
            password: formData.password,
            csrf_token: csrfToken
          }
        },
        {
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }
      )

      // Clear form data after successful submission
      form.reset()

      // Refresh auth state; if Kratos created a session, record signup metric and go home
      const newUser = await refreshSession()
      if (newUser) {
        const email = newUser.traits?.email != null ? String(newUser.traits.email).trim() || undefined : undefined
        if (email != null) {
          try {
            if (typeof Sentry.metrics?.count === 'function') {
              console.debug('[Sentry] Sending user_signup metric', { email })
              Sentry.metrics.count('user_signup', 1, { attributes: { email } })
              await Sentry.flush(2000)
              console.debug('[Sentry] user_signup metric sent and flushed')
            } else {
              console.warn('[Sentry] metrics.count not available (SDK may need upgrade for metrics)')
            }
          } catch (metricErr) {
            console.error('[Sentry] Failed to send signup metric', metricErr)
          }
        }
        window.location.href = '/'
      } else {
        window.location.href = '/login?from=signup&status=success'
      }
    } catch (err: any) {
      console.error('Registration failed:', err.response || err)
      
      // Extract error messages from Ory response
      let errorMessage = 'Registration failed. Please check your input and try again.'
      
      if (err.response?.data?.ui?.messages) {
        const messages = err.response.data.ui.messages
        const errorMessages = messages
          .filter((msg: any) => msg.type === 'error')
          .map((msg: any) => msg.text)
        
        if (errorMessages.length > 0) {
          errorMessage = errorMessages.join(' ')
        }
      } else if (err.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message
      }
      
      setErrorMessage(errorMessage)
      setIsSubmitting(false)
    }
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.handleSubmit(onSubmit)()
  }

  return (
    <div className="light space-y-6" style={{ colorScheme: 'light' }}>
      <div className="text-center mb-6">
        <h1 className="text-3xl text-gray-900 mb-2">Create your account</h1>
      </div>
      
      <div className="space-y-6">
        {/* Social Login Buttons */}
        <div className="grid gap-3">
          <Button 
            variant="outline" 
            className="w-full border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-black hover:shadow-lg transition-all duration-200"
            onClick={() => startOidcRegistration()}
            disabled={isGoogleLoading}
            type="button"
          >
            {isGoogleLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Connecting to Google...
              </>
            ) : (
              <>
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </>
            )}
          </Button>
        </div>

        {/* Separator */}
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator className="w-full bg-gray-300" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-white px-2 text-gray-500">
              Or continue with
            </span>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={handleFormSubmit} method="POST" className="space-y-6" noValidate>
            {errorMessage && (
              <Alert variant="destructive" className="bg-red-50 border-red-200 text-red-800">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-900">Full Name</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Your Name" 
                      required 
                      autoComplete="name"
                      {...field} 
                      className="border-gray-300 bg-white text-gray-900 placeholder:text-gray-500"
                      suppressHydrationWarning
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-900">Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter your email address"
                      type="email"
                      required
                      autoComplete="email"
                      {...field}
                      className="border-gray-300 bg-white text-gray-900 placeholder:text-gray-500"
                      suppressHydrationWarning
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-900">Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        placeholder="Enter password"
                        type={showPassword ? "text" : "password"}
                        required
                        minLength={6}
                        autoComplete="new-password"
                        {...field}
                        className="border-gray-300 bg-white text-gray-900 placeholder:text-gray-500"
                        suppressHydrationWarning
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Terms Checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="terms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
                className="border-gray-300 bg-white"
              />
              <label
                htmlFor="terms"
                className="text-xs text-gray-600"
              >
                I accept the{' '}
                <Link
                  href="https://fastfold.ai/terms-of-use"
                  className="text-blue-600 hover:text-blue-700"
                >
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link
                  href="https://fastfold.ai/privacy-policy"
                  className="text-blue-600 hover:text-blue-700"
                >
                  Privacy Policy
                </Link>
              </label>
            </div>

            <SignupButton isSubmitting={isSubmitting} />

          </form>
        </Form>
      </div>
      
      <div className="flex flex-col items-center gap-2">
        <Link
          href="/login"
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          Already have an account? Log in
        </Link>
      </div>
    </div>
  )
}

export default function SignupForm() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SignupFormContent />
    </Suspense>
  )
}

function SignupButton({ isSubmitting = false }: { isSubmitting?: boolean }) {
  const { pending } = useFormStatus()
  const loading = isSubmitting || pending

  return (
    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={loading}>
      {loading ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Creating account...
        </>
      ) : (
        'Sign Up'
      )}
    </Button>
  )
}
